import React, { useEffect, useState, useCallback } from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Chip,
  CircularProgress,
  CardActionArea
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import DescriptionIcon from '@mui/icons-material/Description';
import AssignmentIcon from '@mui/icons-material/Assignment';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import ArticleIcon from '@mui/icons-material/Article';
import WorkHistoryIcon from '@mui/icons-material/WorkHistory';
import AssignmentTurnedInIcon from '@mui/icons-material/AssignmentTurnedIn';
import CampaignIcon from '@mui/icons-material/Campaign';
import { apiFetch } from '../../api/client';
import EvolucaoSolicitacoesChart from './EvolucaoSolicitacoesChart';
import { buildContratosDashboardQuery } from '../../utils/contratosDashboardUtils';

async function fetchApi(path) {
  const url = path.startsWith('/') ? path : `/${path}`;
  const res = await apiFetch(url);
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    throw new Error(data?.error || `Erro ${res.status}`);
  }
  return data;
}

const formatNumber = (value) => new Intl.NumberFormat('pt-BR').format(value || 0);

const cardBaseSx = {
  position: 'relative',
  overflow: 'hidden',
  borderRadius: 3,
  border: '1px solid rgba(255,255,255,0.6)',
  boxShadow: '0 4px 20px rgba(0,0,0,0.06), 0 0 0 1px rgba(0,0,0,0.02)',
  transition: 'transform 0.25s ease, box-shadow 0.25s ease',
  cursor: 'pointer',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    height: '1px',
    background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.8), transparent)',
    opacity: 0.8
  },
  '&:hover': {
    transform: 'translateY(-6px)',
    boxShadow: '0 12px 32px rgba(0,0,0,0.12), 0 0 0 1px rgba(0,0,0,0.04)'
  }
};

export default function ContratosDashboardCards({ unidade, filtroDashboard }) {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [metrics, setMetrics] = useState(null);
  const [evolucaoSolicitacoes, setEvolucaoSolicitacoes] = useState(null);
  const [loadingEvolucaoSolicitacoes, setLoadingEvolucaoSolicitacoes] = useState(true);
  const [anoGraficoContratos, setAnoGraficoContratos] = useState(new Date().getFullYear());

  const resolvedFiltro = filtroDashboard || { unidade: unidade || undefined };

  const buildQuery = useCallback(
    (extra = {}) => buildContratosDashboardQuery({ ...resolvedFiltro, ...extra }),
    [resolvedFiltro]
  );

  const loadMetrics = useCallback(async () => {
    setLoading(true);
    try {
      const qs = buildQuery();
      const url = qs ? `/api/contratos/dashboard-metrics?${qs}` : '/api/contratos/dashboard-metrics';
      const data = await fetchApi(url);
      setMetrics(data);
    } catch (error) {
      console.error('Erro ao carregar métricas do dashboard de contratos:', error);
      setMetrics(null);
    } finally {
      setLoading(false);
    }
  }, [buildQuery]);

  const loadEvolucaoSolicitacoes = useCallback(async () => {
    setLoadingEvolucaoSolicitacoes(true);
    try {
      const qs = buildQuery({ ano: anoGraficoContratos });
      const data = await fetchApi(`/api/dashboard-contratos/evolucao-solicitacoes?${qs}`);
      setEvolucaoSolicitacoes(data);
    } catch (error) {
      console.error('Erro ao carregar evolução por solicitações:', error);
      setEvolucaoSolicitacoes(null);
    } finally {
      setLoadingEvolucaoSolicitacoes(false);
    }
  }, [buildQuery, anoGraficoContratos]);

  useEffect(() => {
    loadMetrics();
  }, [loadMetrics]);

  useEffect(() => {
    loadEvolucaoSolicitacoes();
  }, [loadEvolucaoSolicitacoes]);

  const safeMetrics = metrics || {
    contratos: { total: 0 },
    solicitacoes: { total: 0 },
    notificacoes: { fiscal: 0, terceiros: 0 },
    termosReferencia: { total: 0 },
    processosContratacao: {
      total: 0,
      andamento: { emAndamento: 0, concluido: 0, cancelado: 0 }
    },
    termosCompromisso: { total: 0 },
    atosConvocatorios: { total: 0 }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" py={6}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Grid container spacing={2}>
        <Grid item xs={12} sm={6} md={3} lg={3}>
          <Card
            sx={{
              ...cardBaseSx,
              background: 'linear-gradient(145deg, rgba(25,118,210,0.14) 0%, rgba(66,165,245,0.08) 50%, rgba(255,255,255,0.9) 100%)',
              '&:hover': { ...cardBaseSx['&:hover'], boxShadow: '0 12px 32px rgba(25,118,210,0.2)' }
            }}
          >
            <CardActionArea onClick={() => navigate('/contratos')} sx={{ p: 0 }}>
              <CardContent sx={{ '&:last-child': { pb: 2 } }}>
                <Box display="flex" alignItems="center" justifyContent="space-between">
                  <Box>
                    <Typography variant="subtitle2" color="primary" sx={{ fontWeight: 600, textTransform: 'uppercase', fontSize: '0.75rem', letterSpacing: '0.05em' }}>
                      Quantidade de Contratos
                    </Typography>
                    <Typography variant="h4" sx={{ fontWeight: 700, color: '#0d47a1', mt: 0.5 }}>
                      {formatNumber(safeMetrics.contratos?.total)}
                    </Typography>
                  </Box>
                  <Box sx={{ backgroundColor: 'rgba(25,118,210,0.18)', borderRadius: 2, p: 1.5, display: 'flex', boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.3)' }}>
                    <DescriptionIcon sx={{ color: 'primary.main', fontSize: 28 }} />
                  </Box>
                </Box>
              </CardContent>
            </CardActionArea>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3} lg={3}>
          <Card
            sx={{
              ...cardBaseSx,
              background: 'linear-gradient(145deg, rgba(123,31,162,0.14) 0%, rgba(156,39,176,0.08) 50%, rgba(255,255,255,0.9) 100%)',
              '&:hover': { ...cardBaseSx['&:hover'], boxShadow: '0 12px 32px rgba(123,31,162,0.2)' }
            }}
          >
            <CardActionArea onClick={() => navigate('/contratos/solicitacoes')} sx={{ p: 0 }}>
              <CardContent sx={{ '&:last-child': { pb: 2 } }}>
                <Box display="flex" alignItems="center" justifyContent="space-between">
                  <Box>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600, textTransform: 'uppercase', fontSize: '0.75rem', letterSpacing: '0.05em', color: '#6a1b9a' }}>
                      Solicitações
                    </Typography>
                    <Typography variant="h4" sx={{ fontWeight: 700, color: '#4a148c', mt: 0.5 }}>
                      {formatNumber(safeMetrics.solicitacoes?.total)}
                    </Typography>
                  </Box>
                  <Box sx={{ backgroundColor: 'rgba(123,31,162,0.18)', borderRadius: 2, p: 1.5, display: 'flex', boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.3)' }}>
                    <AssignmentIcon sx={{ color: '#7b1fa2', fontSize: 28 }} />
                  </Box>
                </Box>
              </CardContent>
            </CardActionArea>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3} lg={3}>
          <Card
            sx={{
              ...cardBaseSx,
              background: 'linear-gradient(145deg, rgba(255,87,34,0.14) 0%, rgba(255,112,67,0.08) 50%, rgba(255,255,255,0.9) 100%)',
              '&:hover': { ...cardBaseSx['&:hover'], boxShadow: '0 12px 32px rgba(255,87,34,0.2)' }
            }}
          >
            <CardActionArea onClick={() => navigate('/contratos/solicitacoes')} sx={{ p: 0 }}>
              <CardContent sx={{ '&:last-child': { pb: 2 } }}>
                <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
                  <Box>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600, textTransform: 'uppercase', fontSize: '0.75rem', letterSpacing: '0.05em', color: '#e65100' }}>
                      Notificações
                    </Typography>
                    <Typography variant="h5" sx={{ fontWeight: 700, color: '#bf360c', mt: 0.5 }}>
                      {formatNumber((safeMetrics.notificacoes?.fiscal || 0) + (safeMetrics.notificacoes?.terceiros || 0))}
                    </Typography>
                  </Box>
                  <Box sx={{ backgroundColor: 'rgba(255,87,34,0.18)', borderRadius: 2, p: 1.5, display: 'flex', boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.3)' }}>
                    <NotificationsActiveIcon sx={{ color: '#f4511e', fontSize: 28 }} />
                  </Box>
                </Box>
                <Typography variant="body2" color="text.secondary">Para fiscal: <strong>{formatNumber(safeMetrics.notificacoes?.fiscal)}</strong></Typography>
                <Typography variant="body2" color="text.secondary">Para terceiro: <strong>{formatNumber(safeMetrics.notificacoes?.terceiros)}</strong></Typography>
              </CardContent>
            </CardActionArea>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3} lg={3}>
          <Card
            sx={{
              ...cardBaseSx,
              background: 'linear-gradient(145deg, rgba(46,125,50,0.14) 0%, rgba(76,175,80,0.08) 50%, rgba(255,255,255,0.9) 100%)',
              '&:hover': { ...cardBaseSx['&:hover'], boxShadow: '0 12px 32px rgba(46,125,50,0.2)' }
            }}
          >
            <CardActionArea onClick={() => navigate('/contratos/solicitacoes')} sx={{ p: 0 }}>
              <CardContent sx={{ '&:last-child': { pb: 2 } }}>
                <Box display="flex" alignItems="center" justifyContent="space-between">
                  <Box>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600, textTransform: 'uppercase', fontSize: '0.75rem', letterSpacing: '0.05em', color: '#2e7d32' }}>
                      Termos de Referência
                    </Typography>
                    <Typography variant="h4" sx={{ fontWeight: 700, color: '#1b5e20', mt: 0.5 }}>
                      {formatNumber(safeMetrics.termosReferencia?.total)}
                    </Typography>
                  </Box>
                  <Box sx={{ backgroundColor: 'rgba(76,175,80,0.18)', borderRadius: 2, p: 1.5, display: 'flex', boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.3)' }}>
                    <ArticleIcon sx={{ color: '#388e3c', fontSize: 28 }} />
                  </Box>
                </Box>
              </CardContent>
            </CardActionArea>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={4} lg={4}>
          <Card
            sx={{
              ...cardBaseSx,
              background: 'linear-gradient(145deg, rgba(33,150,243,0.14) 0%, rgba(3,169,244,0.08) 50%, rgba(255,255,255,0.9) 100%)',
              '&:hover': { ...cardBaseSx['&:hover'], boxShadow: '0 12px 32px rgba(33,150,243,0.2)' }
            }}
          >
            <CardActionArea onClick={() => navigate('/contratos/processo-contratacao')} sx={{ p: 0 }}>
              <CardContent sx={{ '&:last-child': { pb: 2 } }}>
                <Box display="flex" alignItems="center" justifyContent="space-between">
                  <Box>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600, textTransform: 'uppercase', fontSize: '0.75rem', letterSpacing: '0.05em', color: '#1565c0' }}>
                      Processos de Contratação
                    </Typography>
                    <Typography variant="h4" sx={{ fontWeight: 700, color: '#0d47a1', mt: 0.5 }}>
                      {formatNumber(safeMetrics.processosContratacao?.total)}
                    </Typography>
                  </Box>
                  <Box sx={{ backgroundColor: 'rgba(33,150,243,0.18)', borderRadius: 2, p: 1.5, display: 'flex', boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.3)' }}>
                    <WorkHistoryIcon sx={{ color: '#1976d2', fontSize: 28 }} />
                  </Box>
                </Box>
                <Box mt={2} display="flex" gap={1} flexWrap="wrap">
                  <Chip label={`Em andamento: ${formatNumber(safeMetrics.processosContratacao?.andamento?.emAndamento)}`} size="small" color="info" variant="outlined" />
                  <Chip label={`Concluídos: ${formatNumber(safeMetrics.processosContratacao?.andamento?.concluido)}`} size="small" color="success" variant="outlined" />
                  <Chip label={`Cancelados: ${formatNumber(safeMetrics.processosContratacao?.andamento?.cancelado)}`} size="small" color="error" variant="outlined" />
                </Box>
              </CardContent>
            </CardActionArea>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={4} lg={4}>
          <Card
            sx={{
              ...cardBaseSx,
              background: 'linear-gradient(145deg, rgba(255,214,0,0.14) 0%, rgba(255,235,59,0.08) 50%, rgba(255,255,255,0.9) 100%)',
              '&:hover': { ...cardBaseSx['&:hover'], boxShadow: '0 12px 32px rgba(255,193,7,0.2)' }
            }}
          >
            <CardActionArea sx={{ p: 0 }}>
              <CardContent sx={{ '&:last-child': { pb: 2 } }}>
                <Box display="flex" alignItems="center" justifyContent="space-between">
                  <Box>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600, textTransform: 'uppercase', fontSize: '0.75rem', letterSpacing: '0.05em', color: '#f9a825' }}>
                      Termos de Compromisso
                    </Typography>
                    <Typography variant="h4" sx={{ fontWeight: 700, color: '#f57f17', mt: 0.5 }}>
                      {formatNumber(safeMetrics.termosCompromisso?.total)}
                    </Typography>
                  </Box>
                  <Box sx={{ backgroundColor: 'rgba(255,214,0,0.25)', borderRadius: 2, p: 1.5, display: 'flex', boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.3)' }}>
                    <AssignmentTurnedInIcon sx={{ color: '#f9a825', fontSize: 28 }} />
                  </Box>
                </Box>
              </CardContent>
            </CardActionArea>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={4} lg={4}>
          <Card
            sx={{
              ...cardBaseSx,
              background: 'linear-gradient(145deg, rgba(63,81,181,0.14) 0%, rgba(92,107,192,0.08) 50%, rgba(255,255,255,0.9) 100%)',
              '&:hover': { ...cardBaseSx['&:hover'], boxShadow: '0 12px 32px rgba(63,81,181,0.2)' }
            }}
          >
            <CardActionArea sx={{ p: 0 }}>
              <CardContent sx={{ '&:last-child': { pb: 2 } }}>
                <Box display="flex" alignItems="center" justifyContent="space-between">
                  <Box>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600, textTransform: 'uppercase', fontSize: '0.75rem', letterSpacing: '0.05em', color: '#303f9f' }}>
                      Atos Convocatórios / Editais
                    </Typography>
                    <Typography variant="h4" sx={{ fontWeight: 700, color: '#283593', mt: 0.5 }}>
                      {formatNumber(safeMetrics.atosConvocatorios?.total)}
                    </Typography>
                  </Box>
                  <Box sx={{ backgroundColor: 'rgba(63,81,181,0.18)', borderRadius: 2, p: 1.5, display: 'flex', boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.3)' }}>
                    <CampaignIcon sx={{ color: '#3949ab', fontSize: 28 }} />
                  </Box>
                </Box>
              </CardContent>
            </CardActionArea>
          </Card>
        </Grid>
      </Grid>

      <Box sx={{ mt: 3 }}>
        {loadingEvolucaoSolicitacoes ? (
          <Box display="flex" justifyContent="center" py={4}>
            <CircularProgress size={32} />
          </Box>
        ) : (
          <EvolucaoSolicitacoesChart
            dados={evolucaoSolicitacoes}
            loading={loadingEvolucaoSolicitacoes}
            ano={anoGraficoContratos}
            onAnoChange={(v) => setAnoGraficoContratos(v)}
          />
        )}
      </Box>
    </Box>
  );
}
